/**
 * Primary-biosample selection, shared between the frontend and the reports service.
 *
 * These live here rather than in the frontend because the finalised report filename
 * (see `buildReportFileName`) embeds the primary biosample's biomaterial id, and that
 * filename has to be byte-identical whether it is produced by a user download in the
 * browser or by the cron that emails the report and files it into Labmatrix.
 *
 * The parameter type is structural and deliberately minimal so callers can keep
 * passing their own richer biosample types unchanged.
 */
export interface IBiosampleSelectable {
  sampleType: string;
  biosampleStatus: string;
  biosampleType: string;
}

export function getTumourBiosample<T extends IBiosampleSelectable>(
  biosamples: T[],
): T | undefined {
  const wgsTumour = biosamples.find(
    (biosample) => (
      biosample.sampleType === 'wgs'
      && biosample.biosampleStatus === 'tumour'
      && biosample.biosampleType === 'dna'
    ),
  );
  const panelTumour = biosamples.find(
    (biosample) => (
      biosample.sampleType === 'panel'
      && biosample.biosampleStatus === 'tumour'
      && biosample.biosampleType === 'dna'
    ),
  );
  const liquidBiopsy = biosamples.find(
    (biosample) => (
      biosample.biosampleStatus === 'liqbiopsy'
      && biosample.biosampleType === 'dna'
    ),
  );

  return wgsTumour || panelTumour || liquidBiopsy;
}

export function getGermlineBiosample<T extends IBiosampleSelectable>(
  biosamples: T[],
): T | undefined {
  const wgsNormal = biosamples.find(
    (biosample) => (
      biosample.sampleType === 'wgs'
      && biosample.biosampleStatus === 'normal'
      && biosample.biosampleType === 'dna'
    ),
  );
  const panelNormal = biosamples.find(
    (biosample) => (
      biosample.sampleType === 'panel'
      && biosample.biosampleStatus === 'normal'
      && biosample.biosampleType === 'dna'
    ),
  );

  return wgsNormal || panelNormal;
}

export function getRnaBiosample<T extends IBiosampleSelectable>(
  biosamples: T[],
): T | undefined {
  const rna = biosamples.find(
    (biosample) => (
      biosample.sampleType === 'rnaseq'
      && biosample.biosampleType === 'rna'
    ),
  );

  const panelRNA = biosamples.find(
    (biosample) => (
      biosample.sampleType === 'panel'
      && biosample.biosampleType === 'rna'
    ),
  );

  return rna || panelRNA;
}

export function getMethBiosample<T extends IBiosampleSelectable>(
  biosamples: T[],
): T | undefined {
  return biosamples.find(
    (biosample) => biosample.sampleType === 'methylation',
  );
}

export function getHtsBiosamples<T extends IBiosampleSelectable>(
  biosamples: T[],
): T[] {
  return biosamples.filter(
    (biosample) => biosample.sampleType === 'hts',
  );
}

export function getPrimaryBiosample<T extends IBiosampleSelectable>(
  biosamples: T[],
): T | undefined {
  const tumour = getTumourBiosample(biosamples);
  const normal = getGermlineBiosample(biosamples);
  const rna = getRnaBiosample(biosamples);
  const meth = getMethBiosample(biosamples);
  const hts = getHtsBiosamples(biosamples)[0];

  return tumour || normal || rna || meth || hts;
}
